import api from '@/apis'
import { sortDateRangeParams } from '@/utils/dateRange'
import { createLatestOnly } from '@/utils/latestOnly'
import { createDataVersion } from '@/utils/dataVersion'

// REQUEST OWNERSHIP — "is my response still the newest of its kind?" (utils/latestOnly.js).
// One counter per request KIND, and each is claimed only by the action that owns it. A token is
// never claimed on someone else's behalf: round 8 had the delete claim `list_request` to mean
// "the data changed", which is a different question and invalidated newer reads that were about
// something else entirely. That question now has its own mechanism, below.
//
// The read carries one extra obligation the other views do not: the booking import awaits it and
// needs to know whether the list on screen ended up current, so a request that loses the race
// answers with the WINNER's result instead of guessing.
const list_request = createLatestOnly()
const delete_request = createLatestOnly()

// DATA CURRENCY — "does this snapshot reflect the deletes the server has already applied?"
// (utils/dataVersion.js). Advanced by a successful MUTATION, read by a READ before it commits.
const booking_data = createDataVersion()

let latest_list_attempt = Promise.resolve(true)

// Internal marker only — never returned to a caller.
const SUPERSEDED = Symbol('superseded')

export default {
  state: {
    selected_date_range: [],
    headers: [
      { text: '#', value: 'index', sortable: true, width: 80 },
      { text: 'Import Date', value: 'import_datetime', sortable: true },
      { text: 'Last Name', value: 'lastname', sortable: true, width: 120 },
      { text: 'Booking Date Time', value: 'booking_datetime', sortable: true },
      { text: 'Last 4 digits of phone number', value: 'tel', sortable: true },
      { text: 'Userform Submit ID', value: 'booking_id', sortable: true },
      // `filterable: false` on every column whose cell is produced by a helper. Vuetify's search
      // reads the RAW item key, which for these is either a slot name no row has (`profile`,
      // `created_ticket`) or a value that is not what the operator sees (`ticket_decision` holds
      // `walkin`, the cell reads "Walk-in (if now)"). Leaving them filterable meant typing a word
      // that is plainly on screen emptied the table.
      { text: 'Profile / Service Type', value: 'profile', sortable: false, filterable: false },
      { text: 'Window Status', value: 'booking_window_status', sortable: true },
      { text: 'Ticket Decision', value: 'ticket_decision', sortable: true, filterable: false },
      { text: 'Created Ticket', value: 'created_ticket', sortable: false, filterable: false },
      { text: 'Action', value: 'action', sortable: false }
    ],
    items: []
  },
  mutations: {
    SET_BOOKING_ITEMS(state, payload) {
      state.items = payload
    },
    SET_BOOKING_SELECTED_DATE_RANGE(state, payload) {
      state.selected_date_range = payload
    }
  },
  actions: {
    // Returns whether the list on screen is current when this call settles.
    //
    // A call that is overtaken by a newer one cannot answer that from its own response, and
    // guessing was wrong in BOTH directions: answering `true` let the import announce success over
    // a newer request that had just failed, and answering `false` made it warn the list was stale
    // when the newer request had just refreshed it. So the loser waits for the winner and returns
    // the winner's answer. (If the winner never settles, neither does this — the same exposure as
    // any un-timed-out request in this app; `src/apis/axios.js` sets no timeout.)
    //
    // `silent: true` suppresses every snackbar this action would commit. The import uses it: with
    // the shared loading/success messages in play, the import's verdict was the last message only
    // by luck of timing, and any refresh landing afterwards buried it again. Silent means the
    // import flow commits exactly ONE snackbar — its own verdict — which is a promise this module
    // can actually keep. (It cannot promise that nothing the OPERATOR starts afterwards will
    // overwrite it; the dialog's own summary alert is the durable channel for that.)
    async getBookingRecordsByDateRange({ commit }, payload) {
      // `payload` may be omitted or explicitly null (a caller forwarding a cleared picker range).
      const { silent = false, ...params } = payload || {}
      // Two refreshes can be in flight at once — the screen fetches on mount and on every picker
      // change, and the import fires its own afterwards — and responses can arrive out of order.
      // A stale one would overwrite the newer list, so only the newest may commit anything.
      const isLatest = list_request()
      // Records the currency this snapshot will be reconciled against. Taken BEFORE the request
      // goes out, because that is the moment the server's view of the world is fixed for it.
      const read = booking_data.beginRead()
      const say = payload => {
        if (!silent) commit('SET_SNACKBAR', payload)
      }
      const attempt = (async () => {
        // Once the server has ANSWERED, a local failure is not a server failure — see
        // deleteBookingRecordByID. `commit` runs Vuex subscribers and a throwing one propagates.
        let answered = false
        try {
          say({ status: true, color: 'loading', text: 'Loading reports...', loading: true })
          // Reversed picker order would become an empty BETWEEN — see utils/dateRange.js.
          const response = await api.getBookingByDate(sortDateRangeParams(params))
          answered = true
          // Superseded: a newer request owns the list and its own messaging. Nothing is committed
          // and no claim is made about the list — the newer request is the one that can make it.
          if (!isLatest()) return SUPERSEDED
          if (response.data.success) {
            say({ status: true, color: 'success', text: 'Success', icon: 'check', timeout: '2000' })
            // RECONCILE FORWARD, not discard. Subtracts exactly the rows a delete removed AFTER
            // this request went out — the only rows a pre-delete snapshot can wrongly contain.
            // Everything else is committed as the server sent it, so this really is a current
            // list and `true` below is the truth.
            commit(
              'SET_BOOKING_ITEMS',
              read.reconcile(response.data.data, booking => booking.booking_id)
            )
            return true
          }
          say({ status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
          return false
        } catch (error) {
          if (!isLatest()) return SUPERSEDED
          if (!answered) say({ status: true, color: 'error', text: 'Server Occur Error', icon: 'close', timeout: '2000' })
          return false
        } finally {
          // EVERY exit — success, failure, superseded, throw. A read that never released its slot
          // would pin the ledger open for the life of the tab.
          read.settle()
        }
      })()

      latest_list_attempt = attempt
      let outcome = await attempt
      // Overtaken: adopt the answer of whichever READ is current by the time we get here.
      //
      // `list_request()` is claimed here and nowhere else — `tests/wiring.test.mjs` fails if any
      // other site claims it — and the claim and the `latest_list_attempt` assignment happen with
      // no await between them. So being superseded means a NEWER READ exists and is the promise
      // below, and the chain always ends at a request that committed or failed: attempts are only
      // ever appended, and the last one created is never superseded.
      //
      // ADOPTION IS BY IDENTITY, NOT BY A HOP BUDGET. Round 9 first bounded this at 32 hops, and a
      // review lens measured the cost: 33 legitimate supersessions (a slow backend plus an operator
      // who keeps moving the date picker) exhausted the budget and answered "the list is stale" over
      // a list that was perfectly current — worse than the code it replaced, which answered `true`.
      // The self-check is the exact question instead: the only way to spin for ever is to adopt
      // yourself, which is round 8's microtask livelock (awaiting an already-settled promise
      // resolves on the microtask queue and starves the event loop, so no timer can interrupt it).
      // Every other hop moves strictly forward through the attempt sequence, so it terminates.
      while (outcome === SUPERSEDED) {
        const winner = latest_list_attempt
        // Unreachable while the invariant above holds; `false` is the honest answer if it ever
        // stops holding, because this call established nothing.
        if (winner === attempt) return false
        outcome = await winner
      }
      return outcome
    },
    async deleteBookingRecordByID({ commit, rootState }, payload) {
      // MUTATION, not a read. The server has already done this; being superseded cannot undo it,
      // so a SUCCESS must always reconcile local state. Only the DISPLAY — dialog visibility and
      // snackbar text — is ownership-gated. (Round 7 applied the read rule here and the UI kept
      // showing rows the server had deleted.) See utils/latestOnly.js.
      const isLatest = delete_request()
      const say = snackbar => {
        if (isLatest()) commit('SET_SNACKBAR', snackbar)
      }
      // Once the server has ANSWERED, a local failure is not a server failure. `commit` runs Vuex
      // subscribers and a throwing one propagates, so everything after the response — reconciling,
      // closing, even the snackbar itself — sits inside this try. Reporting "Server Error" there
      // tells the operator their change failed when the server accepted it, which is the most
      // damaging thing this action can say. The request's own failure still reports normally.
      let answered = false
      try {
        say({ status: true, color: 'loading', text: 'Deleting...', loading: true })
        const response = await api.deleteBooking(payload)
        answered = true
        if (response.data.success) {
          // RECONCILE — always. This row is gone from the server whether or not a newer delete
          // has finished in the meantime.
          // RECORDED FIRST, BEFORE ANY commit. DATA CURRENCY, recorded — not read ownership,
          // claimed. A list GET started BEFORE this delete still carries a pre-delete snapshot and
          // would resurrect this row; a GET started after it, or one about a different date range,
          // carries nothing this delete makes stale. Recording the removal lets each read subtract
          // exactly what it must and commit the rest, instead of every in-flight read being thrown
          // away.
          //
          // The ORDER is load-bearing, not tidiness: `commit` runs Vuex subscribers (persisted
          // state, devtools) and a throwing subscriber propagates out of it. Recording after the
          // commit meant the row could be spliced away locally, the record skipped, and the action
          // fall into its own catch — leaving a stale snapshot free to put the row back while the
          // operator was told "Server Occur Error" about a delete that had succeeded. Nothing
          // between the server's success and this line may throw.
          //
          // Recorded even when the row was not in the local list: the server deleted it either
          // way, and a snapshot in flight may well still have it.
          booking_data.removed(payload.params.booking_id)
          const booking_list = [...rootState.bookings.items]
          const index = booking_list.findIndex(booking => booking.booking_id === payload.params.booking_id)
          // `splice(-1, 1)` would delete the LAST booking in the table. A refresh can legitimately
          // have removed this row already, so "not found" means there is nothing left to remove.
          if (index !== -1) {
            booking_list.splice(index, 1)
            commit('SET_BOOKING_ITEMS', booking_list)
          }
          say({ status: true, color: 'success', text: 'Success', icon: 'check', timeout: '2000' })
        } else {
          // Nothing happened on the server, so there is nothing to reconcile — only to report.
          say({ status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
        }
      } catch (error) {
        if (!answered) say({ status: true, color: 'error', text: 'Server Occur Error', icon: 'close', timeout: '2000' })
      }
    }
  }
}
